<template>
    <section id="candidates-swiper" class="candidates-section">
        <!-- Section Header -->
        <div class="section-header">
            <div class="header-decoration">
                <div class="decoration-line left"></div>
                <div class="header-icon">
                    <i class="bi bi-stars"></i>
                </div>
                <div class="decoration-line right"></div>
            </div>
            <h2 class="section-title">Meet Our Candidates</h2>
            <p class="section-subtitle">Swipe to discover the talented contestants</p>
        </div>

        <!-- Swiper Container -->
        <div v-show="!isFetching" class="swiper-container">
            <swiper :modules="[Pagination, Autoplay, EffectCards]" :loop="true" :centered-slides="true" :pagination="{
                clickable: true,
                bulletClass: 'custom-bullet',
                bulletActiveClass: 'custom-bullet-active'
            }" :autoplay="{
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            }" :effect="'cards'" :grabCursor="true" :breakpoints="{
                320: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                },
                640: {
                    slidesPerView: 1.25,
                    spaceBetween: 30,
                },
                768: {
                    slidesPerView: 1.8,
                    spaceBetween: 40,
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 50,
                },
                1440: {
                    slidesPerView: 4,
                    spaceBetween: 60,
                }
            }">
                <swiper-slide v-for="(candidate, index) in candidates" :key="index">
                    <div class="candidate-card" :class="`candidate-${(index % 6) + 1}`">
                        <!-- Card Glow Effect -->
                        <div class="card-glow"></div>

                        <!-- Image Container -->
                        <div class="image-container">
                            <div class="image-frame">
                                <img :src="`${apiConfig.baseUrl}${candidate.profilePic}`" :alt="candidate.name"
                                    class="candidate-image" @error="handleImageError" loading="lazy" />
                                <div class="image-overlay">
                                    <div class="overlay-content">
                                        <span class="view-profile">View Profile</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Candidate Number Badge -->
                            <div class="candidate-badge">
                                <span class="badge-number">{{ candidate.userPK }}</span>
                            </div>
                        </div>

                        <!-- Card Content -->
                        <div class="card-content">
                            <div class="candidate-info">
                                <h3 class="candidate-name">{{ candidate.name }}</h3>
                                <div class="candidate-agency">
                                    <i class="bi bi-geo-alt-fill"></i>
                                    <span>{{ candidate.agency }}</span>
                                </div>
                            </div>

                            <!-- Social/Stats Bar -->
                            <div class="stats-bar">
                                <div class="stat-item">
                                    <i class="bi bi-heart-fill"></i>
                                    <span class="stat-value">_</span>
                                </div>
                                <div class="stat-item">
                                    <i class="bi bi-star-fill"></i>
                                    <span class="stat-value">_</span>
                                </div>
                                <div class="stat-item">
                                    <i class="bi bi-trophy-fill"></i>
                                    <span class="stat-value">_</span>
                                </div>
                            </div>
                        </div>

                        <!-- Hover Effect Border -->
                        <div class="hover-border"></div>
                    </div>
                </swiper-slide>
            </swiper>
        </div>
        <div v-show="isFetching" class="loading-container">
            <!-- Animated Crown Loader -->
            <div class="crown-loader">
                <div class="crown">
                    <div class="crown-top">
                        <div class="crown-spike"></div>
                        <div class="crown-spike"></div>
                        <div class="crown-spike"></div>
                        <div class="crown-spike"></div>
                        <div class="crown-spike"></div>
                    </div>
                    <div class="crown-base"></div>
                </div>
                <div class="crown-glow"></div>
            </div>

            <!-- Floating Candidate Cards Skeleton -->
            <div class="skeleton-cards">
                <div class="skeleton-card" v-for="n in 1" :key="n" :style="`--delay: ${n * 0.1}s`">
                    <div class="skeleton-image"></div>
                    <div class="skeleton-content">
                        <div class="skeleton-line skeleton-name"></div>
                        <div class="skeleton-line skeleton-agency"></div>
                        <div class="skeleton-stats">
                            <div class="skeleton-stat"></div>
                            <div class="skeleton-stat"></div>
                            <div class="skeleton-stat"></div>
                        </div>
                    </div>
                    <div class="skeleton-glow"></div>
                </div>
            </div>

            <!-- Loading Text -->
            <div class="loading-text">
                <h3 class="loading-title">Loading Candidates</h3>
                <p class="loading-subtitle">Preparing the stage for our talented contestants</p>
                <div class="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        </div>
        <!-- Navigation Instructions -->
        <div class="navigation-hint">
            <div class="hint-content">
                <i class="bi bi-arrow-left-right"></i>
                <span>Swipe or use navigation dots to browse candidates</span>
            </div>
        </div>
    </section>
</template>

<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay, EffectCards } from "swiper/modules";
import { ref, onMounted, watch } from "vue";
import dashboardService from "@/services/dashboardService";
import { apiConfig } from "@/utils/config";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-cards";

//eslint-disable-next-line
const props = defineProps({
    id: {
        type: String,
        required: true,
        default: null
    }
});

const isFetching = ref(false);
const candidates = ref([]);
const useTemplateSlider = () => {
    candidates.value = [
        {
            userPK: "01",
            name: "Sample Candidate 01",
            agency: "Sample Agency 01",
            profilePic: "female-place-holder.jpg"
        },
        {
            userPK: "02",
            name: "Sample Candidate 02",
            agency: "Sample Agency 02",
            profilePic: "female-place-holder.jpg"
        },
        {
            userPK: "03",
            name: "Sample Candidate 03",
            agency: "Sample Agency 03",
            profilePic: "female-place-holder.jpg"
        },
        {
            userPK: "04",
            name: "Sample Candidate 04",
            agency: "Sample Agency 04",
            profilePic: "female-place-holder.jpg"
        }
    ];
}
// Fetch candidates data
const getCandidates = async () => {
    if (!props.id)
        useTemplateSlider();
    try {
        isFetching.value = true;
        const resp = await dashboardService.getCandidates(props.id);
        console.log(resp);
        if (resp === null || resp.length === 0)
            useTemplateSlider();
        else
            candidates.value = resp;
    } catch (error) {
        console.error('Error fetching candidates:', error);
        // Fallback data for demo
        useTemplateSlider();
    }
    finally {
        isFetching.value = false;
    }
};

watch(() => props.id,
    (newVal) => {
        newVal;
        getCandidates();
    }, { immediate: true });

// Handle image loading errors
const handleImageError = (event) => {
    event.target.src = '/female-place-holder.jpg';
};

// Fetch data when the component is mounted
onMounted(() => {
    getCandidates();
});
</script>

<style scoped>
/* Loading Container */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    padding: 40px 20px;
    position: relative;
}

/* Crown Loader */
.crown-loader {
    position: relative;
    width: 80px;
    height: 80px;
    margin-bottom: 40px;
}

.crown {
    position: relative;
    width: 100%;
    height: 100%;
    animation: crownBounce 2s ease-in-out infinite;
}

.crown-top {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 40px;
    display: flex;
    justify-content: space-between;
    padding: 0 5px;
}

.crown-spike {
    width: 12px;
    height: 25px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border-radius: 4px 4px 0 0;
    animation: spikeGlow 1.5s ease-in-out infinite;
}

.crown-spike:nth-child(1) {
    animation-delay: 0s;
}

.crown-spike:nth-child(2) {
    animation-delay: 0.1s;
}

.crown-spike:nth-child(3) {
    animation-delay: 0.2s;
}

.crown-spike:nth-child(4) {
    animation-delay: 0.3s;
}

.crown-spike:nth-child(5) {
    animation-delay: 0.4s;
}

.crown-base {
    position: absolute;
    bottom: 0;
    left: 10px;
    right: 10px;
    height: 15px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border-radius: 8px 8px 4px 4px;
}

.crown-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120px;
    height: 120px;
    background: radial-gradient(circle, var(--gold-primary) 0%, transparent 70%);
    opacity: 0.3;
    border-radius: 50%;
    animation: pulseGlow 2s ease-in-out infinite;
}

/* Skeleton Cards */
.skeleton-cards {
    display: flex;
    gap: 30px;
    margin-bottom: 40px;
    flex-wrap: wrap;
    justify-content: center;
}

.skeleton-card {
    width: 280px;
    height: 400px;
    background: rgba(26, 26, 26, 0.8);
    border-radius: 20px;
    border: 1px solid var(--border-gold);
    position: relative;
    overflow: hidden;
    opacity: 0;
    animation: skeletonFadeIn 0.5s ease forwards;
    animation-delay: var(--delay);
}

.skeleton-image {
    width: 100%;
    height: 60%;
    background: linear-gradient(90deg, #2a2a2a 25%, #333 50%, #2a2a2a 75%);
    background-size: 200% 100%;
    animation: skeletonShimmer 2s infinite;
}

.skeleton-content {
    padding: 20px;
}

.skeleton-line {
    height: 12px;
    background: linear-gradient(90deg, #2a2a2a 25%, #333 50%, #2a2a2a 75%);
    background-size: 200% 100%;
    border-radius: 6px;
    margin-bottom: 10px;
    animation: skeletonShimmer 2s infinite;
}

.skeleton-name {
    width: 80%;
    height: 16px;
}

.skeleton-agency {
    width: 60%;
    height: 10px;
}

.skeleton-stats {
    display: flex;
    justify-content: space-around;
    margin-top: 15px;
}

.skeleton-stat {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(90deg, #2a2a2a 25%, #333 50%, #2a2a2a 75%);
    background-size: 200% 100%;
    animation: skeletonShimmer 2s infinite;
}

.skeleton-glow {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.1), transparent);
    animation: skeletonSweep 2s infinite;
    animation-delay: var(--delay);
}

/* Loading Text */
.loading-text {
    text-align: center;
}

.loading-title {
    font-size: 1.8rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 50%, var(--gold-primary) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    margin-bottom: 10px;
}

.loading-subtitle {
    color: var(--text-muted);
    font-size: 1rem;
    margin-bottom: 20px;
}

.loading-dots {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.loading-dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--gold-primary);
    animation: dotBounce 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) {
    animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
    animation-delay: -0.16s;
}

.loading-dots span:nth-child(3) {
    animation-delay: 0s;
}

/* Animations */
@keyframes crownBounce {

    0%,
    100% {
        transform: translateY(0) scale(1);
    }

    50% {
        transform: translateY(-10px) scale(1.05);
    }
}

@keyframes spikeGlow {

    0%,
    100% {
        opacity: 0.7;
        transform: scaleY(1);
    }

    50% {
        opacity: 1;
        transform: scaleY(1.2);
    }
}

@keyframes pulseGlow {

    0%,
    100% {
        opacity: 0.2;
        transform: translate(-50%, -50%) scale(1);
    }

    50% {
        opacity: 0.4;
        transform: translate(-50%, -50%) scale(1.1);
    }
}

@keyframes skeletonFadeIn {
    to {
        opacity: 1;
    }
}

@keyframes skeletonShimmer {
    0% {
        background-position: -200% 0;
    }

    100% {
        background-position: 200% 0;
    }
}

@keyframes skeletonSweep {
    0% {
        left: -100%;
    }

    100% {
        left: 100%;
    }
}

@keyframes dotBounce {

    0%,
    80%,
    100% {
        transform: scale(0.8);
        opacity: 0.5;
    }

    40% {
        transform: scale(1.2);
        opacity: 1;
    }
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
    .loading-container {
        min-height: 50vh;
        padding: 20px 10px;
    }

    .crown-loader {
        width: 60px;
        height: 60px;
        margin-bottom: 30px;
    }

    .skeleton-cards {
        gap: 20px;
    }

    .skeleton-card {
        width: 250px;
        height: 350px;
    }

    .loading-title {
        font-size: 1.5rem;
    }

    .loading-subtitle {
        font-size: 0.9rem;
    }
}

@media (max-width: 480px) {
    .skeleton-cards {
        gap: 15px;
    }

    .skeleton-card {
        width: 220px;
        height: 320px;
    }

    .crown-loader {
        width: 50px;
        height: 50px;
    }
}

/* Main Section */
.candidates-section {
    background: linear-gradient(135deg, var(--dark-bg) 0%, #1a1a1a 100%);
    padding: 40px 20px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Section Header */
.section-header {
    text-align: center;
    margin-bottom: 50px;
    position: relative;
}

.header-decoration {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.decoration-line {
    width: 80px;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--gold-primary), transparent);
    margin: 0 20px;
}

.header-icon {
    width: 60px;
    height: 60px;
    background: rgba(212, 175, 55, 0.1);
    border: 2px solid var(--border-gold);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gold-primary);
    font-size: 1.5rem;
    box-shadow: 0 0 20px var(--shadow-gold);
}

.section-title {
    font-size: 3rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 50%, var(--gold-primary) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 0 30px rgba(212, 175, 55, 0.5);
    margin-bottom: 10px;
    letter-spacing: 2px;
}

.section-subtitle {
    color: var(--text-muted);
    font-size: 1.1rem;
    font-weight: 300;
}

/* Swiper Container */
.swiper-container {
    width: 100%;
    max-width: 1400px;
    padding: 20px 0;
    margin: 0 auto;
    box-sizing: border-box;
    overflow: hidden;
}

.candidate-card {
    position: relative;
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    border: 1px solid var(--border-gold);
    height: 500px;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 300px;
    /* Add max-width to prevent cards from getting too wide */
    margin: 0 auto;
    /* Center the cards */
}

.candidate-card:hover {
    transform: translateY(-15px) scale(1.02);
    box-shadow: 0 20px 60px rgba(212, 175, 55, 0.3);
}

.card-glow {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
            rgba(212, 175, 55, 0.1) 0%,
            transparent 30%,
            transparent 70%,
            rgba(212, 175, 55, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.candidate-card:hover .card-glow {
    opacity: 1;
}

/* Image Container */
.image-container {
    position: relative;
    height: 65%;
    overflow: hidden;
}

.image-frame {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.candidate-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.4s ease;
    filter: brightness(0.9);
}

.candidate-card:hover .candidate-image {
    transform: scale(1.1);
    filter: brightness(1);
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(transparent 60%, rgba(0, 0, 0, 0.8) 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    padding-bottom: 20px;
}

.candidate-card:hover .image-overlay {
    opacity: 1;
}

.overlay-content {
    text-align: center;
}

.view-profile {
    color: var(--text-light);
    background: rgba(212, 175, 55, 0.8);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    backdrop-filter: blur(10px);
}

/* Candidate Badge */
.candidate-badge {
    position: absolute;
    top: 20px;
    left: 20px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 15px rgba(212, 175, 55, 0.4);
    border: 2px solid var(--text-light);
}

.badge-number {
    color: var(--dark-bg);
    font-weight: 700;
    font-size: 1.1rem;
}

/* Card Content */
.card-content {
    padding: 25px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.3));
}

.candidate-info {
    text-align: center;
    margin-bottom: 20px;
}

.candidate-name {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 8px;
    line-height: 1.2;
}

.candidate-agency {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: var(--gold-primary);
    font-size: 0.95rem;
    font-weight: 500;
}

.candidate-agency i {
    font-size: 0.9rem;
}

/* Stats Bar */
.stats-bar {
    display: flex;
    justify-content: space-around;
    background: rgba(0, 0, 0, 0.4);
    padding: 12px;
    border-radius: 15px;
    border: 1px solid var(--border-gold);
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.stat-item i {
    color: var(--gold-primary);
    font-size: 1rem;
}

.stat-value {
    color: var(--text-light);
    font-size: 0.85rem;
    font-weight: 600;
}

/* Hover Border */
.hover-border {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid transparent;
    border-radius: 20px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.candidate-card:hover .hover-border {
    opacity: 0.3;
}

/* Navigation Hint */
.navigation-hint {
    margin-top: 40px;
    text-align: center;
}

.hint-content {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: var(--text-muted);
    background: rgba(255, 255, 255, 0.05);
    padding: 12px 20px;
    border-radius: 25px;
    border: 1px solid var(--border-gold);
    font-size: 0.9rem;
}

.hint-content i {
    color: var(--gold-primary);
}


/* Custom Pagination Bullets */
:deep(.custom-bullet) {
    width: 12px;
    height: 12px;
    background: var(--text-muted);
    opacity: 0.5;
    border-radius: 50%;
    margin: 0 6px;
    cursor: pointer;
    transition: all 0.3s ease;
}

:deep(.custom-bullet-active) {
    background: var(--gold-primary);
    opacity: 1;
    transform: scale(1.2);
    box-shadow: 0 0 10px var(--shadow-gold);
}

/* Candidate Color Variants */
.candidate-1 .candidate-badge {
    background: linear-gradient(135deg, #d4af37, #b8941f);
}

.candidate-2 .candidate-badge {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
}

.candidate-3 .candidate-badge {
    background: linear-gradient(135deg, #3498db, #2980b9);
}

.candidate-4 .candidate-badge {
    background: linear-gradient(135deg, #9b59b6, #8e44ad);
}

.candidate-5 .candidate-badge {
    background: linear-gradient(135deg, #1abc9c, #16a085);
}

.candidate-6 .candidate-badge {
    background: linear-gradient(135deg, #f39c12, #e67e22);
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
    .candidates-section {
        padding: 30px 15px;
    }

    .section-title {
        font-size: 2.2rem;
    }

    .section-subtitle {
        font-size: 1rem;
    }

    .candidate-card {
        height: 450px;
    }

    .card-content {
        padding: 20px;
    }

    .candidate-name {
        font-size: 1.2rem;
    }

    .stats-bar {
        padding: 10px;
    }

    .decoration-line {
        width: 40px;
    }

    .header-icon {
        width: 50px;
        height: 50px;
        font-size: 1.2rem;
    }
}

@media (max-width: 480px) {
    .section-title {
        font-size: 1.8rem;
    }

    .candidate-card {
        height: 400px;
    }

    .candidate-badge {
        width: 40px;
        height: 40px;
        top: 15px;
        left: 15px;
    }

    .badge-number {
        font-size: 1rem;
    }

    .card-content {
        padding: 15px;
    }

    .hint-content {
        font-size: 0.8rem;
        padding: 10px 15px;
    }
}

/* Loading Animation */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.candidate-card {
    animation: fadeInUp 0.6s ease forwards;
}

.candidate-card:nth-child(odd) {
    animation-delay: 0.1s;
}

.candidate-card:nth-child(even) {
    animation-delay: 0.2s;
}
</style>